import React from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Globe, Banknote, Wallet } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import api, { formatINR, formatINRShort } from "@/lib/api";
import { useDash } from "@/pages/dashboard/DashboardLayout";
import { Skeleton } from "@/components/ui/skeleton";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

export default function PaymentsView() {
  const { monthDate, setMonthDate, businessId } = useDash();
  const y = monthDate.getFullYear();
  const m = monthDate.getMonth();
  const changeMonth = (d) => setMonthDate(new Date(y, m + d, 1));

  const { data, isLoading } = useQuery({
    queryKey: ["payments-summary", businessId, y, m],
    queryFn: async () => (await api.get("/payments/summary", { params: { ...(businessId ? { business_id: businessId } : {}), month: m + 1, year: y } })).data,
    enabled: !!businessId,
  });

  if (isLoading) return <Skeleton className="h-96 w-full rounded-xl" />;

  const s = data || { online: 0, cash: 0, unrecorded: 0, total: 0, count: 0, per_venue: [], trend: [], fy_label: "FY" };
  const trendHasData = (s.trend || []).some((t) => t.online > 0 || t.cash > 0);
  const cards = [
    { key: "online", label: "Received online", value: s.online, icon: Globe, color: "#10B981", bg: "bg-pulse-green/10" },
    { key: "cash", label: "Received in cash", value: s.cash, icon: Banknote, color: "#0A192F", bg: "bg-pulse-navy/10" },
    { key: "total", label: "Total received", value: s.total, icon: Wallet, color: "#2563EB", bg: "bg-blue-500/10" },
  ];

  return (
    <div className="space-y-6" data-testid="payments-view">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-pulse-navy">Payment Methods</h1>
          <p className="text-sm text-slate">How much you collected online vs in cash this month.</p>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => changeMonth(-1)} data-testid="pay-prev-month" className="grid h-9 w-9 place-items-center rounded-md border border-slate-200 text-slate transition-colors hover:bg-cloud"><ChevronLeft size={18} /></button>
          <span className="min-w-[130px] text-center text-sm font-semibold text-pulse-navy" data-testid="pay-month-label">{MONTHS[m]} {y}</span>
          <button onClick={() => changeMonth(1)} data-testid="pay-next-month" className="grid h-9 w-9 place-items-center rounded-md border border-slate-200 text-slate transition-colors hover:bg-cloud"><ChevronRight size={18} /></button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        {cards.map((c) => (
          <div key={c.key} className="rounded-xl border border-slate-200 bg-white p-5 shadow-pulse-sm" data-testid={`pay-card-${c.key}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate">{c.label}</span>
              <span className={`grid h-9 w-9 place-items-center rounded-md ${c.bg}`}><c.icon size={18} style={{ color: c.color }} /></span>
            </div>
            <div className="mt-3 text-2xl font-bold text-pulse-navy" data-testid={`pay-value-${c.key}`}>{formatINR(c.value)}</div>
          </div>
        ))}
      </div>

      {s.unrecorded > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="pay-unrecorded-note">
          <b>{formatINR(s.unrecorded)}</b> from older bookings has no payment method recorded. Edit those bookings to tag them as Online or Cash.
        </div>
      )}

      {trendHasData && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-pulse-sm" data-testid="pay-trend-card">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold text-pulse-navy">Online vs Cash · {s.fy_label}</h2>
            <span className="text-sm text-slate">This year — Online <b className="text-pulse-green">{formatINR(s.trend.reduce((a, t) => a + t.online, 0))}</b> · Cash <b className="text-pulse-navy">{formatINR(s.trend.reduce((a, t) => a + t.cash, 0))}</b></span>
          </div>
          <p className="mt-1 text-xs text-slate">Month-by-month payments received online vs in cash across the financial year.</p>
          <div className="mt-4 h-60 min-h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={s.trend} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={formatINRShort} tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E5E7EB", fontSize: 13 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" name="Online" dataKey="online" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3, fill: "#10B981" }} activeDot={{ r: 5 }} />
                <Line type="monotone" name="Cash" dataKey="cash" stroke="#0A192F" strokeWidth={2.5} dot={{ r: 3, fill: "#0A192F" }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-pulse-sm" data-testid="pay-venue-table">
        <h2 className="text-lg font-semibold text-pulse-navy">By venue</h2>
        <p className="mt-1 text-xs text-slate">{s.count} booking{s.count === 1 ? "" : "s"} this month.</p>
        {s.per_venue.length === 0 ? (
          <div className="mt-6 rounded-md bg-cloud py-10 text-center text-sm text-slate" data-testid="pay-empty">No bookings recorded for {MONTHS[m]} {y}.</div>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate">
                  <th className="pb-2 pr-3 font-medium">Venue</th>
                  <th className="pb-2 px-3 text-right font-medium">Online</th>
                  <th className="pb-2 px-3 text-right font-medium">Cash</th>
                  <th className="pb-2 pl-3 text-right font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {s.per_venue.map((v) => (
                  <tr key={v.venue_id} className="border-b border-slate-50 last:border-0" data-testid={`pay-venue-row-${v.venue_id}`}>
                    <td className="py-2.5 pr-3">
                      <span className="inline-flex items-center gap-2 font-medium text-pulse-navy">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: v.color }} />{v.name}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-right text-charcoal">{formatINR(v.online)}</td>
                    <td className="py-2.5 px-3 text-right text-charcoal">{formatINR(v.cash)}</td>
                    <td className="py-2.5 pl-3 text-right font-semibold text-pulse-navy">{formatINR(v.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-200 font-semibold text-pulse-navy">
                  <td className="pt-3 pr-3">Total</td>
                  <td className="pt-3 px-3 text-right">{formatINR(s.online)}</td>
                  <td className="pt-3 px-3 text-right">{formatINR(s.cash)}</td>
                  <td className="pt-3 pl-3 text-right">{formatINR(s.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
